import React from 'react';
import { Box, Tooltip } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ShieldIcon from '@mui/icons-material/Shield';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

/**
 * Visual styling token map for primary reservation status
 */
export const getPrimaryStatusStyle = (rawStatus) => {
    const status = String(rawStatus || 'Confirmed').trim();
    switch (status.toLowerCase()) {
        case 'confirmed':
            return {
                bg: '#EEF2FF',
                color: '#4338CA',
                border: '1px solid #C7D2FE',
                dot: '#4F46E5',
                label: 'Confirmed'
            };
        case 'checked-in':
        case 'in-house':
            return {
                bg: '#ECFDF5',
                color: '#047857',
                border: '1px solid #A7F3D0',
                dot: '#10B981',
                label: status.toLowerCase() === 'in-house' ? 'In-house' : 'Checked-in'
            };
        case 'checked-out':
            return {
                bg: '#F0F9FF',
                color: '#0369A1',
                border: '1px solid #BAE6FD',
                dot: '#0EA5E9',
                label: 'Checked-out'
            };
        case 'cancelled':
            return {
                bg: '#FEF2F2',
                color: '#B91C1C',
                border: '1px solid #FECACA',
                dot: '#EF4444',
                label: 'Cancelled'
            };
        case 'no-show':
            return {
                bg: '#FFF7ED',
                color: '#C2410C',
                border: '1px solid #FFEDD5',
                dot: '#F97316',
                label: 'No-show'
            };
        case 'pending':
            return {
                bg: '#FEFCE8',
                color: '#A16207',
                border: '1px solid #FEF08A',
                dot: '#EAB308',
                label: 'Pending'
            };
        default:
            return {
                bg: '#F8FAFC',
                color: '#475569',
                border: '1px solid #E2E8F0',
                dot: '#94A3B8',
                label: status
            };
    }
};

/**
 * Primary Status Badge with a modern status dot
 */
export const PrimaryStatusBadge = ({ status, size = 'small' }) => {
    const style = getPrimaryStatusStyle(status);
    const isMedium = size === 'medium';

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: isMedium ? 1 : 0.75,
                px: isMedium ? 1.5 : 1.1,
                py: isMedium ? 0.45 : 0.25,
                borderRadius: '16px',
                bgcolor: style.bg,
                color: style.color,
                border: style.border,
                fontSize: isMedium ? '0.8rem' : '0.72rem',
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: '0.01em',
                userSelect: 'none',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                    filter: 'brightness(0.97)'
                }
            }}
        >
            <Box
                sx={{
                    width: isMedium ? 7 : 6,
                    height: isMedium ? 7 : 6,
                    borderRadius: '50%',
                    bgcolor: style.dot,
                    boxShadow: `0 0 0 2px ${style.bg}`
                }}
            />
            <span>{style.label}</span>
        </Box>
    );
};

/**
 * UAE DET Government Regulatory Status Micro-Pill
 */
export const GovernmentStatusBadge = ({ status, primaryStatus, size = 'small' }) => {
    const raw = status || (primaryStatus === 'Pending' ? 'Pending' : 'Approved');
    const norm = String(raw).toLowerCase().trim();
    const isMedium = size === 'medium';

    let config = {
        label: 'Gov: Pending',
        tooltip: 'UAE DET Government Verification: Pending mandatory regulatory clearance before check-in',
        bg: '#FFFBEB',
        color: '#B45309',
        border: '1px solid #FDE68A',
        hoverBg: '#FEF3C7',
        icon: <GppMaybeIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#D97706' }} />
    };

    if (norm === 'approved') {
        config = {
            label: 'Gov: Approved',
            tooltip: 'UAE DET Government Verification: Approved & cleared for guest check-in',
            bg: '#ECFDF5',
            color: '#047857',
            border: '1px solid #A7F3D0',
            hoverBg: '#D1FAE5',
            icon: <VerifiedUserIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#059669' }} />
        };
    } else if (norm === 'rejected') {
        config = {
            label: 'Gov: Rejected',
            tooltip: 'UAE DET Government Verification: Rejected by regulatory authorities',
            bg: '#FEF2F2',
            color: '#B91C1C',
            border: '1px solid #FECACA',
            hoverBg: '#FEE2E2',
            icon: <ShieldIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#DC2626' }} />
        };
    }

    return (
        <Tooltip title={config.tooltip} arrow placement="top">
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: isMedium ? 1.25 : 0.85,
                    py: isMedium ? 0.35 : 0.2,
                    borderRadius: '8px',
                    bgcolor: config.bg,
                    color: config.color,
                    border: config.border,
                    fontSize: isMedium ? '0.75rem' : '0.67rem',
                    fontWeight: 650,
                    lineHeight: 1.2,
                    cursor: 'default',
                    userSelect: 'none',
                    transition: 'all 0.15s ease-in-out',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                        bgcolor: config.hoverBg
                    }
                }}
            >
                {config.icon}
                <span>{config.label}</span>
            </Box>
        </Tooltip>
    );
};

/**
 * Guest Check-In / Web Pre-Arrival Registration Micro-Pill
 */
export const CheckInStatusBadge = ({ status, size = 'small' }) => {
    const norm = String(status || '').toLowerCase().trim();
    const isPreCheckedIn = ['pre-checked-in', 'pre-checked in'].includes(norm);
    const isCheckedIn = ['checked-in', 'checked in'].includes(norm);
    const isMedium = size === 'medium';

    let config = {
        label: 'Check-in: Pending',
        tooltip: 'Guest Web Check-In: Pending (Guest has not yet completed pre-arrival registration online)',
        bg: '#F8FAFC',
        color: '#64748B',
        border: '1px solid #E2E8F0',
        hoverBg: '#F1F5F9',
        icon: <ScheduleIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#94A3B8' }} />
    };

    if (isPreCheckedIn) {
        config = {
            label: 'Pre-Checked In',
            tooltip: 'Guest Web Check-In: Completed (Guest pre-registered and uploaded ID online)',
            bg: '#F0F9FF',
            color: '#0284C7',
            border: '1px solid #BAE6FD',
            hoverBg: '#E0F2FE',
            icon: <HowToRegIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#0284C7' }} />
        };
    } else if (isCheckedIn) {
        config = {
            label: 'Checked In',
            tooltip: 'Guest Check-In: Keys issued and checked into room',
            bg: '#ECFDF5',
            color: '#047857',
            border: '1px solid #A7F3D0',
            hoverBg: '#D1FAE5',
            icon: <CheckCircleOutlineIcon sx={{ fontSize: isMedium ? 15 : 13, color: '#059669' }} />
        };
    }

    return (
        <Tooltip title={config.tooltip} arrow placement="top">
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: isMedium ? 1.25 : 0.85,
                    py: isMedium ? 0.35 : 0.2,
                    borderRadius: '8px',
                    bgcolor: config.bg,
                    color: config.color,
                    border: config.border,
                    fontSize: isMedium ? '0.75rem' : '0.67rem',
                    fontWeight: 650,
                    lineHeight: 1.2,
                    cursor: 'default',
                    userSelect: 'none',
                    transition: 'all 0.15s ease-in-out',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                        bgcolor: config.hoverBg
                    }
                }}
            >
                {config.icon}
                <span>{config.label}</span>
            </Box>
        </Tooltip>
    );
};

/**
 * Composite ReservationStatusBadge for table cells or summary headers
 */
export default function ReservationStatusBadge({
    reservation = {},
    isUae = false,
    direction = 'column',
    size = 'small'
}) {
    const showUaeBadges = Boolean(isUae);

    if (direction === 'row') {
        return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <PrimaryStatusBadge status={reservation.status} size={size} />
                {showUaeBadges && (
                    <>
                        <GovernmentStatusBadge
                            status={reservation.governmentStatus}
                            primaryStatus={reservation.status}
                            size={size}
                        />
                        <CheckInStatusBadge
                            status={reservation.checkInStatus}
                            size={size}
                        />
                    </>
                )}
            </Box>
        );
    }

    // Default 'column' layout for table cells
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: 'flex-start' }}>
            <PrimaryStatusBadge status={reservation.status} size={size} />
            {showUaeBadges && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <GovernmentStatusBadge
                        status={reservation.governmentStatus}
                        primaryStatus={reservation.status}
                        size={size}
                    />
                    <CheckInStatusBadge
                        status={reservation.checkInStatus}
                        size={size}
                    />
                </Box>
            )}
        </Box>
    );
}
